import { useEffect, useState } from 'react';
import { FormControl, MenuItem, Select, type SelectChangeEvent } from '@mui/material';
import { getRegionalOffices, type MetaRegionalOffice } from '~/apis/metaApi';
import { getApiErrorMessage } from '~/apis/cn02FormApi';
import { ALL_REGION } from '~/pages/users/form/types';
import { useSnackbar } from 'notistack';

export function formatRegionalOfficeLabel(office: MetaRegionalOffice): string {
  const districts = office.districts?.length ? office.districts.join(', ') : '';
  return districts ? `${office.code} (${districts})` : office.code;
}

type RegionalOfficeSelectProps = {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  labelId?: string;
  label?: string;
  size?: 'small' | 'medium';
  fullWidth?: boolean;
  error?: boolean;
};

export function RegionalOfficeSelect({
  value,
  onChange,
  disabled = false,
  labelId = 'regional-office-label',
  label = 'Regional office',
  size = 'small',
  fullWidth = true,
  error = false,
}: RegionalOfficeSelectProps) {
  const { enqueueSnackbar } = useSnackbar();
  const [regionalOffices, setRegionalOffices] = useState<MetaRegionalOffice[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    void getRegionalOffices()
      .then((items) => {
        if (active) setRegionalOffices(items);
      })
      .catch((err) => {
        if (active) {
          enqueueSnackbar(getApiErrorMessage(err, 'Could not load regional offices.'), {
            variant: 'error',
          });
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [enqueueSnackbar]);

  const handleChange = (e: SelectChangeEvent<string>) => {
    onChange(e.target.value);
  };

  return (
    <FormControl size={size} fullWidth={fullWidth} error={error} disabled={disabled || loading}>
      <Select
        labelId={labelId}
        label={label}
        value={value}
        onChange={handleChange}
      >
        <MenuItem value={ALL_REGION}>All</MenuItem>
        {regionalOffices.map((office) => (
          <MenuItem key={office.code} value={office.code}>
            {formatRegionalOfficeLabel(office)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
